import type { CartLine, CartTotals, MenuItem } from '../types';

/** Apply a percentage discount. */
export function discounted(price: number, pct: number): number {
  return price - (price * pct) / 100;
}

/**
 * The ceiling used when no settings have loaded yet. The real one comes from
 * `app_settings.max_discount_pct`; this only guards the very first render.
 */
export const DEFAULT_MAX_DISCOUNT_PCT = 90;

/**
 * Clamp any user-typed discount into the range the restaurant allows.
 * `max` comes from settings, so the owner can raise or lower it without a build.
 */
export function clampPct(
  input: string | number,
  max: number = DEFAULT_MAX_DISCOUNT_PCT,
): number {
  const n = typeof input === 'number' ? input : parseInt(input, 10);
  if (!Number.isFinite(n)) return 0;
  const ceiling = Number.isFinite(max) ? Math.max(0, Math.min(100, max)) : DEFAULT_MAX_DISCOUNT_PCT;
  return Math.max(0, Math.min(ceiling, Math.trunc(n)));
}

export function itemMinPrice(item: MenuItem): number {
  return Math.min(...Object.values(item.prices));
}

/** Unit price for one variant of an item, with that item's offer applied. */
export function effectivePrice(item: MenuItem, variant: string, offerPct: number): number {
  const base = item.prices[variant];
  return offerPct > 0 ? discounted(base, offerPct) : base;
}

export function cartTotals(cart: CartLine[], orderDiscount: number): CartTotals {
  let sub = 0;
  let itemSavings = 0;
  let qty = 0;
  for (const line of cart) {
    sub += line.price * line.qty;
    itemSavings += (line.basePrice - line.price) * line.qty;
    qty += line.qty;
  }
  const disc = (sub * orderDiscount) / 100;
  return { sub, disc, grand: sub - disc, itemSavings, qty };
}

export function cartItemCount(cart: CartLine[]): number {
  return cart.reduce((s, c) => s + c.qty, 0);
}
